import { Button } from "@mui/material";
import {
  ArrowLeftRight,
  AtSign,
  BadgeDollarSign,
  Bell,
  ChartNoAxesCombined,
  ChevronDown,
  ClipboardClock,
  DatabaseZap,
  Gamepad,
  Gamepad2,
  GamepadDirectional,
  Gift,
  Hash,
  Image,
  Images,
  Layers2,
  LayoutDashboard,
  MessageCircleHeart,
  MoveLeft,
  PencilRuler,
  Settings,
  Smartphone,
  SquareArrowOutUpRight,
  Star,
  TicketCheck,
  User,
  Wallet,
  Webhook,
  Zap,
} from "lucide-react";

import { AppStoreLogo, Gear, Globe, Note, UsersThree } from "phosphor-react";
import React, { useState, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAppContext } from "../util/AppProvider";
import { useUserStore } from "../../store/useUserStore";

const menuItems = [
  {
    id: "dashboard",
    icon: LayoutDashboard,
    label: "Dashboard",
  },
  {
    id: "apps",
    icon: AppStoreLogo,
    label: "Apps",
  },
  {
    id: "teams",
    icon: UsersThree,
    label: "Teams",
  },
  {
    id: "payout",
    icon: Wallet,
    label: "Payout",
    subMenu: [
      { id: "analytics", label: "Analytics", icon: ChartNoAxesCombined },
      { id: "gateways", label: "Gateways", icon: BadgeDollarSign },
      { id: "giftcodes", label: "Gift Codes", icon: Gift },
      { id: "transactions", label: "Transactions", icon: ArrowLeftRight },
      { id: "configs", label: "Configurations", icon: Zap },
    ],
  },
  {
    id: "media",
    icon: Images,
    label: "Media",
    subMenu: [{ id: "images", label: "Images", icon: Image }],
  },
  { id: "socials", icon: AtSign, label: "Socials" },
  { id: "ads_revenue", icon: BadgeDollarSign, label: "Ad Revanue" },
  { id: "sponsor_apps", icon: GamepadDirectional, label: "Sponsor Apps" },
  { id: "games", icon: Gamepad2, label: "Games" },

  { id: "influencer", icon: MessageCircleHeart, label: "Influencer" },
  { id: "notification", icon: Bell, label: "Notifications" },
];

const appMenu = [
  {
    group: "Overview",
    items: [
      { id: "analytics", icon: ChartNoAxesCombined, label: "Analytics" },
      { id: "reviews", icon: Star, label: "App Review" },
    ],
  },
  {
    group: "App",
    items: [
      { id: "devices", icon: Smartphone, label: "Devices" },
      { id: "users", icon: User, label: "Users" },
      { id: "transactions", icon: ClipboardClock, label: "Transactions" },
      {
        id: "settings",
        icon: Gear,
        label: "Settings",
        subMenu: [
          { id: "configs", label: "Configurations", icon: Zap },
          { id: "callback", label: "Callback", icon: Webhook },
          { id: "bonus", label: "Bonus", icon: Gamepad },
          { id: "payout", label: "Payout", icon: Wallet },
          { id: "redirect", label: "Redirect", icon: SquareArrowOutUpRight },
          { id: "withdrawal", label: "Withdrawal", icon: TicketCheck },
          { id: "socials", label: "Socials", icon: AtSign },
          {
            id: "sponsor_apps",
            label: "Sponsor Apps",
            icon: GamepadDirectional,
          },
        ],
      },
    ],
  },
  {
    group: "Website & Tools",
    items: [
      {
        id: "website",
        icon: Globe,
        label: "Website",
        subMenu: [
          { id: "pages", label: "Pages", icon: Layers2 },
          { id: "setting", label: "Settings", icon: Settings },
        ],
      },
      { id: "server", icon: DatabaseZap, label: "Server Info" },
    ],
  },
  {
    group: "System",
    items: [
      { id: "rule", icon: PencilRuler, label: "Rule" },
      { id: "note", icon: Note, label: "Notes" },
    ],
  },
];

function SideBar({ collapsed, onToggleHome }) {
  const [expandedItems, setExpandedItems] = useState([]);
  const [isHovered, setIsHovered] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const effectiveCollapsed = collapsed && !isHovered;

  const toggleExpanded = (id) => {
    setExpandedItems((prev) => (prev.includes(id) ? [] : [id]));
  };

  const avatar = useUserStore((state) => state.avatar);
  const name = useUserStore((state) => state.name);
  const role = useUserStore((state) => state.role);

  const { selectedApp, setSelectedApp } = useAppContext();
   const appIcon = selectedApp?.icon || "https://media.adwave.in/logos/avatars/console-logo.jpg";
  useEffect(() => {
    const settingsMatch = location.pathname.match(/^\/apps\/[^/]+\/settings/);
    if (settingsMatch) {
      setExpandedItems(["settings"]);
    } else {
      setExpandedItems([]);
    }
  }, [location.pathname]);

  useEffect(() => {
    const m = location.pathname.match(/^\/apps\/([^/]+)/);
    if (m) {
      setSelectedApp({ id: m[1], name: m[1] ,icon: appIcon}); // Set the selected app based on the URL
    } else {
      setSelectedApp(null);
    }
  }, [location.pathname]);

  const renderItem = (item, basePath) => {
    const isActive = location.pathname.startsWith(`${basePath}/${item.id}`);

    return (
      <div key={item.id}>
        <button
          onClick={() => {
            if (item.subMenu) {
              toggleExpanded(item.id);
            } else {
              navigate(`${basePath}/${item.id}`);
            }
          }}
          className={`w-full flex items-center justify-between px-4 py-2 rounded-lg ${
            isActive
              ? "bg-blue-500/30 text-white"
              : "text-slate-600 hover:bg-slate-100"
          }`}
        >
          <div className="flex items-center space-x-2">
            <item.icon className="w-4 h-4" />
            {!effectiveCollapsed && (
              <span className="text-xs font-semibold">{item.label}</span>
            )}
          </div>

          {!effectiveCollapsed && item.subMenu && (
            <ChevronDown
              className={`w-4 h-4 ${
                expandedItems.includes(item.id) ? "rotate-180" : ""
              }`}
            />
          )}
        </button>

        {/* SUBMENU */}
        {!effectiveCollapsed &&
          item.subMenu &&
          expandedItems.includes(item.id) && (
            <div className="ml-8 mt-2 space-y-1">
              {item.subMenu.map((sub) => (
                <NavLink
                  key={sub.id}
                  to={`${basePath}/${item.id}/${sub.id}`}
                  className={({ isActive }) =>
                    `flex items-center p-2 text-xs rounded-lg ${
                      isActive
                        ? "bg-blue-500/20 text-blue-600"
                        : "text-slate-500 hover:bg-slate-100"
                    }`
                  }
                >
                  <sub.icon className="w-4 h-4 mr-2" />
                  {sub.label}
                </NavLink>
              ))}
            </div>
          )}
      </div>
    );
  };

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`${
        effectiveCollapsed ? "w-20" : "w-72"
      } transition-all bg-white dark:bg-slate-900 border-r flex flex-col`}
    >
      {/* LOGO */}
      <div className="p-6 border-b border-slate-500 dark:border-slate-700">
       

        {!selectedApp? (
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                {/* <Zap className="text-white" size={18} /> */}
                  <img
                  className="object-cover rounded-lg"
                    src="https://media.adwave.in/logos/avatars/console-logo.jpg"
                alt="adwave console"
              />
            </div>
            {!effectiveCollapsed && (
              <h1 className="text-lg font-bold text-slate-800 dark:text-slate-200">
                Adwave Console
              </h1>
            )}
          </div>
        ):
        (<div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
            {/* <Zap className="text-white" size={18} /> */}
            <img
              className="object-cover rounded-lg"
              src={selectedApp.icon}
              alt="adwave console"
            />
          </div>
          
          {!effectiveCollapsed && (
            <h1 className="text-md font-semibold text-slate-800 dark:text-slate-200">
            {decodeURIComponent(decodeURIComponent(selectedApp.name))}
              
            </h1>
          )}
        </div>)
        }

      </div>

      {/* MENU */}
      <nav className="flex-1 p-4 space-y-4 overflow-y-auto">
        {selectedApp
          ? appMenu.map((group, i) => (
              <div key={i}>
                {!effectiveCollapsed && (
                  <p className="text-xs text-slate-400 mb-2">{group.group}</p>
                )}
                {group.items.map((item) =>
                  renderItem(item, `/apps/${selectedApp.id}`),
                )}
              </div>
            ))
          : menuItems.map((item) => renderItem(item, ""))}
      </nav>

      {/* BACK BUTTON */}
      {selectedApp && (
        <Button
          onClick={() => {
            setSelectedApp(null);
            onToggleHome?.();
            navigate("/apps");
          }}
        >
          <MoveLeft />
          {!effectiveCollapsed && "Back"}
        </Button>
      )}

      {/* USER */}
      <div
        onClick={() => navigate("/account")}
        className="p-4 border-t cursor-pointer"
      >
        <div className="flex items-center space-x-2">
          <img src={avatar} className="w-10 h-10 rounded-full" />
          {!effectiveCollapsed && (
            <div>
              <h1 className="font-semibold text-sm text-slate-500 dark:text-slate-300">
                {name}
              </h1>
              <p className="text-xs text-slate-500">{role}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SideBar;
